import { RSAA, ApiError } from 'redux-api-middleware';
import * as Sentry from '@sentry/react';
import backoff from 'redux/helpers/backoff';
import computeApiState from 'redux/helpers/computeApiState';
import getUsageTrackingUrl from 'url-constructors/getUsageTrackingURL';
import { EntityTypes } from 'context/entityContext';
import { Dispatch } from 'redux';
import { TGetRootState } from 'types/shared';
import getRuntimeEnv from "helpers/env";

const TRACK_ENTITY = 'OPUS/USAGE_STATISTICS/TRACK_ENTITY';

const TRACK_ENTITY_REQUEST = 'OPUS/USAGE_STATISTICS/TRACK_ENTITY_REQUEST';
const TRACK_ENTITY_RETRY = 'OPUS/USAGE_STATISTICS/TRACK_ENTITY_RETRY';
const TRACK_ENTITY_SUCCESS = 'OPUS/USAGE_STATISTICS/TRACK_ENTITY_SUCCESS';
const TRACK_ENTITY_FAILURE = 'OPUS/USAGE_STATISTICS/TRACK_ENTITY_FAILURE';

const INIT_REQUEST = 'OPUS/USAGE_STATISTICS/INIT_REQUEST';
const INIT_SUCCESS = 'OPUS/USAGE_STATISTICS/INIT_SUCCESS';
const INIT_FAILURE = 'OPUS/USAGE_STATISTICS/INIT_FAILURE';

const LOAD_REQUEST = 'OPUS/USAGE_STATISTICS/LOAD_REQUEST';
const LOAD_SUCCESS = 'OPUS/USAGE_STATISTICS/LOAD_SUCCESS';
const LOAD_FAILURE = 'OPUS/USAGE_STATISTICS/LOAD_FAILURE';

const CLEAR_ENTITY_USAGE = 'OPUS/USAGE_STATISTICS/CLEAR_ENTITY_USAGE';

type EntityUsageActionTypes =
  | typeof TRACK_ENTITY
  | typeof TRACK_ENTITY_REQUEST
  | typeof TRACK_ENTITY_RETRY
  | typeof TRACK_ENTITY_SUCCESS
  | typeof TRACK_ENTITY_FAILURE
  | typeof INIT_REQUEST
  | typeof INIT_SUCCESS
  | typeof INIT_FAILURE
  | typeof LOAD_REQUEST
  | typeof LOAD_SUCCESS
  | typeof LOAD_FAILURE
  | typeof CLEAR_ENTITY_USAGE;

interface IEntityUsageAction {
  type: EntityUsageActionTypes;
  payload: any;
}

export interface IEntityUsageState {
  pending: boolean;
  fulfilled: boolean;
  rejected: boolean;
  recentEntities: any[];
}

const initialState: IEntityUsageState = {
  pending: false,
  fulfilled: false,
  rejected: false,
  recentEntities: [],
};

const ENTITY_LIMIT = 20;
const TRACKING_TYPE = 'entity';

export default function reducer(
  state: IEntityUsageState = initialState,
  action: IEntityUsageAction
): IEntityUsageState {
  switch (action.type) {
    case TRACK_ENTITY: {
      const { entityId, entityType } = action.payload;

      const itemIndex = state.recentEntities.findIndex((element) => {
        return element.entityId === entityId;
      });

      const isItemInArray = itemIndex > -1;

      // We'll use this intermediary variable to update the store
      let updatedRecentEntities = state.recentEntities;

      if (isItemInArray) {
        updatedRecentEntities = updatedRecentEntities.map((item) =>
          item.entityId === entityId
            ? {
                ...item,
                visitAmount: item.visitAmount + 1,
                lastVisit: new Date(),
              }
            : item
        );
      } else {
        updatedRecentEntities.unshift({
          entityId,
          entityType,
          visitAmount: 1,
          lastVisit: new Date(),
        });
      }

      // Sort the entities by date, so the most recent ones are on the top
      // This should already be OK when inserting new items with unshift, but it might
      // run out of order when we update items with the map function
      updatedRecentEntities.sort((a, b) => {
        return a.lastVisit > b.lastVisit
          ? -1
          : a.lastVisit < b.lastVisit
          ? 1
          : 0;
      });

      // Cut off everything after the limit we set
      updatedRecentEntities = updatedRecentEntities.slice(0, ENTITY_LIMIT);

      return {
        ...state,
        recentEntities: updatedRecentEntities,
      };
    }

    case CLEAR_ENTITY_USAGE: {
      return {
        ...initialState,
      };
    }

    case TRACK_ENTITY_RETRY: {
      return {
        ...state,
        pending: true,
        fulfilled: false,
      };
    }

    case TRACK_ENTITY_SUCCESS: {
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
      };
    }

    case TRACK_ENTITY_FAILURE:
    case INIT_FAILURE:
    case LOAD_FAILURE:
      return {
        ...state,
        pending: false,
        fulfilled: false,
        rejected: true,
      };

    case INIT_REQUEST:
    case LOAD_REQUEST:
    case TRACK_ENTITY_REQUEST:
      return {
        ...state,
        pending: true,
      };

    case INIT_SUCCESS: {
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
      };
    }

    case LOAD_SUCCESS: {
      return {
        ...state,
        pending: false,
        fulfilled: true,
        rejected: false,
        recentEntities: [
          ...state.recentEntities,
          ...action.payload.recentEntities,
        ],
      };
    }

    default:
      return state;
  }
}

/**
 * This action creator initializes an empty object at a specific component_settings endpoint for a user.
 * Only called once per user, when we get a 404 while trying to load the settings.
 */
function initEntityUsageEndpoint(): (
  dispatch: Dispatch,
  getState: TGetRootState
) => Promise<any> {
  return async (dispatch: Dispatch, getState: TGetRootState): Promise<any> => {
    const { id } = getState().authentication.profile;
    const url = getUsageTrackingUrl(id, TRACKING_TYPE);

    // dispatch the action
    return await dispatch({
      [RSAA]: {
        types: [
          {
            type: INIT_REQUEST,
          },
          {
            type: INIT_SUCCESS,
          },
          INIT_FAILURE,
        ],
        endpoint: url,
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          recentEntities: [],
        }),
      },
    });
  };
}

export const loadRecentEntities = (): ((
  dispatch: Dispatch,
  getState: TGetRootState
) => Promise<any>) => {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    const { entityUsage } = getState();

    const apiStatus = computeApiState(entityUsage);

    // bail out if the apiState is other than unloaded
    if (apiStatus !== 'unloaded') {
      return;
    }

    const { id } = getState().authentication.profile;
    const url = getUsageTrackingUrl(id, TRACKING_TYPE);

    // This returns the settings from the API
    // The reducer will automatically update the store with the settings from the API
    // This way, your component settings will transfer across different devices.
    const actionResponse = await dispatch({
      [RSAA]: {
        types: [LOAD_REQUEST, LOAD_SUCCESS, LOAD_FAILURE],
        endpoint: url,
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'same-origin',
      },
    });

    // This should only happen once for every user
    if (actionResponse.error) {
      if ((actionResponse.payload as ApiError<any>).status === 404) {
        return await dispatch(initEntityUsageEndpoint() as any);
      }
    }

    return actionResponse;
  };
};
/**
 * This action creator reads the store and sends the current state of the store to the KV API
 */
function saveToApi(): (
  dispatch: Dispatch,
  getState: TGetRootState
) => Promise<any> {
  return async function (
    dispatch: Dispatch,
    getState: TGetRootState
  ): Promise<any> {
    const { id } = getState().authentication.profile;
    const { recentEntities } = getState().entityUsage;
    const url = getUsageTrackingUrl(id, TRACKING_TYPE);

    try {
      const response = await backoff(
        async () =>
          await dispatch({
            [RSAA]: {
              types: [
                TRACK_ENTITY_REQUEST,
                TRACK_ENTITY_SUCCESS,
                TRACK_ENTITY_RETRY,
              ],
              endpoint: url,
              method: 'PUT',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'same-origin',
              body: JSON.stringify({ recentEntities }),
            },
          }),
        'An error occurred trying to save your entity usage statistics. Retrying for the %s time...'
      );

      if (response.error) {
        throw response.payload;
      }

      return response;
    } catch (error) {
      // The final retry error will end up here. Log it to console.
      // eslint-disable-next-line no-console
      console.warn('Your recent entities failed to be saved remotely.');
      // eslint-disable-next-line no-console
      console.error(error);

      // Report it to Sentry
      if (getRuntimeEnv().NODE_ENV === 'production') {
        // Send the exception to Sentry
        Sentry.captureException(error);
      }

      // Dispatch a final failed state
      dispatch({ type: TRACK_ENTITY_FAILURE });
    }
  };
}

/**
 * This action creator takes an entityId and entityType
 * It first updates the local reducer before sending off an API request to sync the local redux store to the API
 *
 * @export
 * @param entityId   The ID of the entity you want to track
 * @param entityType The type of the entity you're tracking
 */
export function trackEntity(
  entityId: string,
  entityType: EntityTypes
): (dispatch: Dispatch) => void {
  return async function (dispatch: Dispatch): Promise<any> {
    dispatch({
      type: TRACK_ENTITY,
      payload: {
        entityId,
        entityType,
      },
    });
    dispatch(saveToApi() as any);
  };
}

/**
 * This action reset entity usage and save to the KV API
 */

export function resetEnitityUsage(): (dispatch: Dispatch) => void {
  return async function (dispatch: Dispatch): Promise<any> {
    dispatch({
      type: CLEAR_ENTITY_USAGE,
    });
    dispatch(saveToApi() as any);
  };
}
